import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from './api';
import { errorMessage } from './errors';
import { toast } from '../hooks/use-toast';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { ArrowLeft, Home, User, Mail, Phone, DollarSign, Calendar, MapPin, Loader2, Save, Trash2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PhoneInput from './shared/PhoneInput';
import ContactSearchInput, { FIELD_CLASS } from './shared/ContactSearchInput';
import { contactFields } from './shared/useTextRecipient';
import MoneyInput from './shared/MoneyInput';
import NumberInput from './shared/NumberInput';
import AddressFields from './shared/AddressFields';
import { emptyAddress, addressPayload, addressLine } from './shared/address';
import { formatDateTime } from './kit/time';
import { PROPERTY_TYPE_LABELS, labelFor, optionsFrom } from './shared/labels';
import { STAGES } from './re/stages';
import TransactionTeam from './re/TransactionTeam';
import TransactionShareLink from './re/TransactionShareLink';
import LoanLinkControl from './re/LoanLinkControl';
import { TransactionContracts } from './re/ContractDraft';

// Dates the hub's deadline alerts read (GET /v2/re/alerts); the closing date has its own field above.
const DEADLINE_FIELDS = [
  ['option_period_expires', 'Option Period Ends'],
  ['inspection_date', 'Inspection'],
  ['appraisal_date', 'Appraisal'],
  ['financing_deadline', 'Financing Deadline'],
];

const fmt$ = (n) => n ? Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : '—';

export default function TransactionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tx, setTx] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/re/transactions/${id}`);
      setTx(data);
      setForm({
        stage: data.stage || 'prospect',
        side: data.side || 'buyer',
        contract_price: data.contract_price || '',
        buyer_names: data.buyer_names || '',
        buyer_email: data.buyer_email || '',
        buyer_phone: data.buyer_phone || '',
        seller_names: data.seller_names || '',
        seller_email: data.seller_email || '',
        seller_phone: data.seller_phone || '',
        cooperating_agent_name: data.cooperating_agent_name || '',
        cooperating_agent_email: data.cooperating_agent_email || '',
        cooperating_agent_phone: data.cooperating_agent_phone || '',
        lender_name: data.lender_name || '',
        title_company: data.title_company || '',
        escrow_company: data.escrow_company || '',
        escrow_officer: data.escrow_officer || '',
        closing_date: data.close_date?.substring(0, 10) || '',  // backend field is close_date
        contract_date: data.contract_date?.substring(0, 10) || '',
        option_period_days: data.option_period_days ?? '',
        option_fee: data.option_fee ?? '',
        earnest_money: data.earnest_money ?? '',
        option_period_expires: data.option_period_expires?.substring(0, 10) || '',
        inspection_date: data.inspection_date?.substring(0, 10) || '',
        appraisal_date: data.appraisal_date?.substring(0, 10) || '',
        financing_deadline: data.financing_deadline?.substring(0, 10) || '',
        notes: data.notes || '',
        // Property Address
        property_address: { ...emptyAddress(), ...(data.property_address || {}) },
        // Property details snapshot
        property_type: data.property_type || '',
        beds:          data.beds ?? '',
        baths:         data.baths ?? '',
        sqft:          data.sqft ?? '',
        lot_size_sqft: data.lot_size_sqft ?? '',
        year_built:    data.year_built ?? '',
      });
    } catch {
      toast({ title: 'Transaction not found' });
      navigate('/portal/real-estate');
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // After a rotate or turn-off only the link changed: refresh the deal, keep unsaved edits.
  const refreshShare = async () => {
    try { const { data } = await api.get(`/re/transactions/${id}`); setTx(data); } catch { /* the link action already toasted */ }
  };

  // Linking a loan also sets the lender name on the server, so that field follows it; every other
  // unsaved edit stays.
  const refreshLoanLink = async () => {
    try {
      const { data } = await api.get(`/re/transactions/${id}`);
      setTx(data);
      setForm((x) => ({ ...x, lender_name: data.lender_name || '' }));
    } catch { /* the link action already toasted */ }
  };

  const save = async () => {
    setSaving(true);
    try {
      const clean = (v) => (v === '' || v === undefined) ? null : v;
      const property_address = addressPayload(form.property_address);
      const payload = {
        stage: form.stage,
        side: form.side,
        contract_price: form.contract_price ? Number(form.contract_price) : null,
        buyer_names: clean(form.buyer_names),
        buyer_email: clean(form.buyer_email),
        buyer_phone: clean(form.buyer_phone),
        seller_names: clean(form.seller_names),
        seller_email: clean(form.seller_email),
        seller_phone: clean(form.seller_phone),
        cooperating_agent_name: clean(form.cooperating_agent_name),
        cooperating_agent_email: clean(form.cooperating_agent_email),
        cooperating_agent_phone: clean(form.cooperating_agent_phone),
        lender_name: clean(form.lender_name),
        title_company: clean(form.title_company),
        escrow_company: clean(form.escrow_company),
        escrow_officer: clean(form.escrow_officer),
        close_date: clean(form.closing_date),  // backend field is close_date
        contract_date: clean(form.contract_date),
        option_period_days: form.option_period_days === '' ? null : Number(form.option_period_days),
        option_fee: form.option_fee === '' ? null : Number(form.option_fee),
        earnest_money: form.earnest_money === '' ? null : Number(form.earnest_money),
        option_period_expires: clean(form.option_period_expires),
        inspection_date: clean(form.inspection_date),
        appraisal_date: clean(form.appraisal_date),
        financing_deadline: clean(form.financing_deadline),
        notes: clean(form.notes),
        property_address,
        // Property details — coerce empty string to null so backend clears cleanly
        property_type: clean(form.property_type),
        beds:          form.beds === '' ? null : Number(form.beds),
        baths:         form.baths === '' ? null : Number(form.baths),
        sqft:          form.sqft === '' ? null : Number(form.sqft),
        lot_size_sqft: form.lot_size_sqft === '' ? null : Number(form.lot_size_sqft),
        year_built:    form.year_built === '' ? null : Number(form.year_built),
      };
      await api.patch(`/re/transactions/${id}`, payload);
      toast({ title: 'Saved' });
      load();
    } catch (e) {
      // FastAPI 422 returns detail as an array of pydantic errors — stringify safely.
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
                : Array.isArray(detail) ? detail.map(d => d.msg || d.type).join(', ')
                : detail ? JSON.stringify(detail) : 'Error';
      toast({ title: 'Save failed', description: msg });
    }
    finally { setSaving(false); }
  };

  // Ticket 188: a contact pick fills the buyer name, email and phone; all stay
  // editable. The transaction's own contact_id is the deal's anchor contact (it also
  // covers the listing side and several buyers), so a buyer pick does not change it.
  // A contact missing an email or phone blanks it rather than keep the last person's.
  const pickBuyer = (c) => {
    const f = contactFields(c);
    setForm((x) => ({ ...x, buyer_names: f.name, buyer_email: f.email, buyer_phone: f.phone }));
  };

  const del = async () => {
    if (!(await confirmDialog({ title: 'Delete this transaction?', description: 'This cannot be undone.', confirmText: 'Delete', destructive: true }))) return;
    try {
      await api.delete(`/re/transactions/${id}`);
      toast({ title: 'Deleted' });
      navigate('/portal/real-estate');
    } catch (e) { toast({ title: 'Delete failed', description: errorMessage(e) }); }
  };

  if (!tx) return <div className="p-10 text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</div>;

  const stageInfo = STAGES.find(s => s.key === form.stage);
  const propAddr = addressLine(form.property_address);

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto" data-testid="transaction-detail-page">
      <Link to="/portal/real-estate" className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-700 text-sm mb-4" data-testid="back-to-real-estate">
        <ArrowLeft size={14} /> Back to Real Estate
      </Link>

      {/* Header banner */}
      <div className="rounded-2xl p-6 mb-6" style={{ background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' }}>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/60">Real Estate Transaction</div>
            <div className="font-serif text-2xl font-bold text-white truncate" data-testid="tx-title">
              {form.buyer_names || form.seller_names || 'Transaction'}
            </div>
            {propAddr && <div className="mt-1 text-sm text-white/70 flex items-center gap-1.5"><MapPin size={12} /> {propAddr}</div>}
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-white/60">Contract Price</div>
            <div className="font-serif text-3xl font-bold" style={{ color: '#A6E56C' }} data-testid="tx-price">{fmt$(form.contract_price)}</div>
            {stageInfo && (
              <div className="mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold" style={{ background: stageInfo.color + '40', color: '#fff' }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: stageInfo.color }} />
                {stageInfo.label}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Left main panel */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm"><TransactionTeam transactionId={id} /></div>

          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="tx-share-section">
            <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: '#6BA82E' }}>Client Status Link</div>
            <p className="text-[11px] text-slate-500 mb-3">A read-only page where the buyer or seller can follow this deal. Anyone with the link can open it.</p>
            <TransactionShareLink tx={tx} onChange={refreshShare} />
          </div>

          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm"><TransactionContracts tx={tx} /></div>

          {/* Buyer / Seller */}
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <div className="text-[10px] uppercase tracking-widest font-bold mb-3" style={{ color: '#6BA82E' }}>Parties</div>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><Label>Buyer Name(s)</Label><ContactSearchInput value={form.buyer_names} onChange={(v) => setForm((x) => ({ ...x, buyer_names: v }))} onPick={pickBuyer} testid="tx-buyer-names" placeholder="Type to find a contact" className={`${FIELD_CLASS} mt-1`} /></div>
              <div><Label>Buyer Email</Label><Input value={form.buyer_email} onChange={(e) => setForm({ ...form, buyer_email: e.target.value })} className="mt-1" data-testid="tx-buyer-email" /></div>
              <div><Label>Buyer Phone</Label><PhoneInput value={form.buyer_phone} onChange={(d) => setForm({ ...form, buyer_phone: d })} className="mt-1" data-testid="tx-buyer-phone" /></div>
              <div className="md:col-span-2"><Label>Seller Name(s)</Label><Input value={form.seller_names} onChange={(e) => setForm({ ...form, seller_names: e.target.value })} className="mt-1" data-testid="tx-seller-names" /></div>
              <div><Label>Seller Email</Label><Input value={form.seller_email} onChange={(e) => setForm({ ...form, seller_email: e.target.value })} className="mt-1" data-testid="tx-seller-email" /></div>
              <div><Label>Seller Phone</Label><PhoneInput value={form.seller_phone} onChange={(d) => setForm({ ...form, seller_phone: d })} className="mt-1" data-testid="tx-seller-phone" /></div>
              <div className="md:col-span-2"><Label>Cooperating Agent</Label><Input value={form.cooperating_agent_name} onChange={(e) => setForm({ ...form, cooperating_agent_name: e.target.value })} className="mt-1" placeholder="Agent name · Brokerage" data-testid="tx-coop-name" /></div>
              <div><Label>Cooperating Agent Email</Label><Input value={form.cooperating_agent_email} onChange={(e) => setForm({ ...form, cooperating_agent_email: e.target.value })} className="mt-1" data-testid="tx-coop-email" /></div>
              <div><Label>Cooperating Agent Phone</Label><PhoneInput value={form.cooperating_agent_phone} onChange={(d) => setForm({ ...form, cooperating_agent_phone: d })} className="mt-1" data-testid="tx-coop-phone" /></div>
            </div>
          </div>

          {/* Lender / title / escrow */}
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="tx-closing-team">
            <div className="text-[10px] uppercase tracking-widest font-bold mb-3" style={{ color: '#6BA82E' }}>Lender, Title &amp; Escrow</div>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Lender</Label><Input value={form.lender_name} onChange={(e) => setForm({ ...form, lender_name: e.target.value })} className="mt-1" placeholder="Top Choice Lending / other" data-testid="tx-lender-name" /></div>
              <div><Label>Title Company</Label><Input value={form.title_company} onChange={(e) => setForm({ ...form, title_company: e.target.value })} className="mt-1" data-testid="tx-title-company" /></div>
              <div><Label>Escrow Company</Label><Input value={form.escrow_company} onChange={(e) => setForm({ ...form, escrow_company: e.target.value })} className="mt-1" data-testid="tx-escrow-company" /></div>
              <div><Label>Escrow Officer</Label><Input value={form.escrow_officer} onChange={(e) => setForm({ ...form, escrow_officer: e.target.value })} className="mt-1" data-testid="tx-escrow-officer" /></div>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100" data-testid="tx-loan-link-section">
              <Label>Linked Loan File</Label>
              <p className="text-[11px] text-slate-500 mt-0.5 mb-2">Link the loan this deal is financed through. The client&apos;s status page then shows the loan&apos;s progress.</p>
              <LoanLinkControl tx={tx} onChange={refreshLoanLink} />
            </div>
          </div>

          {/* Property Address */}
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <div className="text-[10px] uppercase tracking-widest font-bold mb-3" style={{ color: '#6BA82E' }}>Subject Property</div>
            <AddressFields value={form.property_address} onChange={(a) => setForm({ ...form, property_address: a })} testIdPrefix="tx-addr" label="" />

            {/* Google Maps embed — appears once street + city are filled. Uses the
                keyless /maps?q=&output=embed endpoint which supports any query string. */}
            {(form.property_address?.street && form.property_address?.city) && (
              <div className="mt-4 rounded-xl overflow-hidden border border-slate-200" data-testid="tx-map-embed">
                <iframe
                  title="Property location"
                  src={`https://www.google.com/maps?q=${encodeURIComponent(addressLine(form.property_address) || '')}&output=embed`}
                  width="100%"
                  height="260"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
                <div className="px-3 py-2 bg-slate-50 border-t border-slate-100 flex items-center justify-between text-[11px]">
                  <span className="text-slate-500">
                    <MapPin size={11} className="inline -mt-0.5 mr-1 text-slate-400" />
                    {addressLine(form.property_address)}
                  </span>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressLine(form.property_address) || '')}`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold hover:underline"
                    style={{ color: '#070B5F' }}
                    data-testid="tx-map-open-in-maps"
                  >Open in Google Maps ↗</a>
                </div>
              </div>
            )}
          </div>

          {/* Property Details — listing-style snapshot */}
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="tx-property-details">
            <div className="text-[10px] uppercase tracking-widest font-bold mb-3" style={{ color: '#6BA82E' }}>Property Details</div>
            <div className="grid md:grid-cols-6 gap-3">
              <div className="md:col-span-2">
                <Label>Property Type</Label>
                <Select value={form.property_type || 'none'} onValueChange={(v) => setForm({ ...form, property_type: v === 'none' ? '' : v })}>
                  <SelectTrigger className="mt-1" data-testid="tx-property-type"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {optionsFrom(PROPERTY_TYPE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-1"><Label>Beds</Label><NumberInput integer value={form.beds === '' ? null : form.beds} onChange={(n) => setForm({ ...form, beds: n ?? '' })} className="mt-1" data-testid="tx-beds" placeholder="3" /></div>
              <div className="md:col-span-1"><Label>Baths</Label><NumberInput value={form.baths === '' ? null : form.baths} onChange={(n) => setForm({ ...form, baths: n ?? '' })} className="mt-1" data-testid="tx-baths" placeholder="2.5" /></div>
              <div className="md:col-span-1"><Label>Sq Ft</Label><NumberInput integer value={form.sqft === '' ? null : form.sqft} onChange={(n) => setForm({ ...form, sqft: n ?? '' })} className="mt-1" data-testid="tx-sqft" placeholder="1,800" /></div>
              <div className="md:col-span-1"><Label>Year Built</Label><NumberInput integer min={1800} max={2100} value={form.year_built === '' ? null : form.year_built} onChange={(n) => setForm({ ...form, year_built: n ?? '' })} className="mt-1" data-testid="tx-year-built" placeholder="2005" /></div>
              <div className="md:col-span-2"><Label>Lot Size (sq ft)</Label><NumberInput integer value={form.lot_size_sqft === '' ? null : form.lot_size_sqft} onChange={(n) => setForm({ ...form, lot_size_sqft: n ?? '' })} className="mt-1" data-testid="tx-lot-size" placeholder="7,500" /></div>
            </div>

            {/* Quick summary chip strip — renders once at least beds/baths/sqft are filled */}
            {(form.beds || form.baths || form.sqft || form.year_built) && (
              <div className="mt-4 flex flex-wrap gap-2 text-[11px]" data-testid="tx-property-summary">
                {form.beds     && <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 font-semibold">{form.beds} bd</span>}
                {form.baths    && <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 font-semibold">{form.baths} ba</span>}
                {form.sqft     && <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 font-semibold">{Number(form.sqft).toLocaleString()} sqft</span>}
                {form.lot_size_sqft && <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 font-semibold">{Number(form.lot_size_sqft).toLocaleString()} sqft lot</span>}
                {form.year_built && <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 font-semibold">Built {form.year_built}</span>}
                {form.property_type && <span className="px-2 py-1 rounded-full font-semibold" style={{ background: '#070B5F15', color: '#070B5F' }}>{labelFor(PROPERTY_TYPE_LABELS, form.property_type)}</span>}
              </div>
            )}
          </div>

          {/* Financials + dates */}
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <div className="text-[10px] uppercase tracking-widest font-bold mb-3" style={{ color: '#6BA82E' }}>Deal Terms</div>
            <div className="grid md:grid-cols-3 gap-3">
              <div><Label>Contract Price</Label><MoneyInput value={form.contract_price} onChange={(n) => setForm({ ...form, contract_price: n ?? '' })} className="mt-1" data-testid="tx-price-input" /></div>
              <div><Label>Closing Date</Label><Input type="date" value={form.closing_date} onChange={(e) => setForm({ ...form, closing_date: e.target.value })} className="mt-1" data-testid="tx-close-date" /></div>
              <div><Label>Side</Label>
                <Select value={form.side} onValueChange={(v) => setForm({ ...form, side: v })}>
                  <SelectTrigger className="mt-1" data-testid="tx-side"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="buyer">Buyer Rep</SelectItem>
                    <SelectItem value="listing">Listing</SelectItem>
                    <SelectItem value="dual">Dual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Contract milestones: the dates here feed the hub's deadline alerts; the money and option days fill a drafted contract. */}
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="tx-milestones">
            <div className="text-[10px] uppercase tracking-widest font-bold mb-3" style={{ color: '#6BA82E' }}>Milestones</div>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Contract Date</Label><Input type="date" value={form.contract_date || ''} onChange={(e) => setForm({ ...form, contract_date: e.target.value })} className="mt-1" data-testid="tx-contract_date" /></div>
              <div><Label>Option Period (days)</Label><NumberInput integer min={0} value={form.option_period_days === '' ? null : form.option_period_days} onChange={(n) => setForm({ ...form, option_period_days: n ?? '' })} className="mt-1" data-testid="tx-option_period_days" placeholder="7" /></div>
              <div><Label>Earnest Money</Label><MoneyInput value={form.earnest_money} onChange={(n) => setForm({ ...form, earnest_money: n ?? '' })} className="mt-1" data-testid="tx-earnest_money" /></div>
              <div><Label>Option Fee</Label><MoneyInput value={form.option_fee} onChange={(n) => setForm({ ...form, option_fee: n ?? '' })} className="mt-1" data-testid="tx-option_fee" /></div>
              {DEADLINE_FIELDS.map(([key, label]) => (
                <div key={key}><Label>{label}</Label><Input type="date" value={form[key] || ''} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="mt-1" data-testid={`tx-${key}`} /></div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <div className="text-[10px] uppercase tracking-widest font-bold mb-3" style={{ color: '#6BA82E' }}>Notes</div>
            <Textarea rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Deal history, contingencies, follow-ups…" />
          </div>
        </div>

        {/* Right rail — stage picker + save */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <div className="text-[10px] uppercase tracking-widest font-bold mb-3" style={{ color: '#6BA82E' }}>Stage</div>
            <div className="flex flex-wrap gap-1.5">
              {STAGES.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setForm({ ...form, stage: s.key })}
                  data-testid={`tx-stage-${s.key}`}
                  className="text-[11px] font-semibold px-2 py-1 rounded-md border transition hover:brightness-110"
                  style={form.stage === s.key
                    ? { background: s.color, color: 'white', borderColor: s.color }
                    : { background: 'white', color: s.color, borderColor: s.color + '50' }}
                >{s.label}</button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm space-y-2">
            <button
              onClick={save}
              disabled={saving}
              data-testid="tx-save"
              className="w-full text-[13px] font-semibold px-4 py-2 rounded-lg text-white flex items-center justify-center gap-1.5"
              style={{ background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' }}
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
            <button
              onClick={del}
              data-testid="tx-delete"
              className="w-full text-[12px] font-semibold px-4 py-2 rounded-lg text-red-600 hover:bg-red-50 flex items-center justify-center gap-1.5 border border-red-200"
            >
              <Trash2 size={12} /> Delete Transaction
            </button>
          </div>

          <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100 text-[11px] text-slate-500">
            <div className="font-semibold text-slate-700 mb-1">Meta</div>
            <div>Transaction ID: <span className="font-mono">{tx.transaction_id}</span></div>
            {tx.created_at && <div>Created: {formatDateTime(tx.created_at)}</div>}
            {tx.updated_at && <div>Updated: {formatDateTime(tx.updated_at)}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
